// Curriculum Section Variants Configuration
// FAQ, Program Structure, Elective, Program Fee
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import {
  setFaq1,
  setFaq2,
  setVisionaries1,
  setFaq3,
  setFaq4,
  setProgramStructure1,
  setElective1,
  setProgramFee1,
  setProgramFee1043,
  setProgramFee2,
  setProgramStructure2,
  setProgramStructure3,
  setProgramStructure4,
  setProgramStructure5,
  setProgramStructure1042,
  setProgramStructure1064,
  setElective1065,
  setProgramFee1066,
  setFaq1033,
  setFaq5013,
} from "@/store/slices/page";
import {
  Form_1,
  Form_3,
  Form_6,
  Form_7,
  Form_8,
  Form_9,
} from "@/components/organisms/admin/forms";
import type { PageState } from "@/store/slices/page";
import { FAQ_1_CONSTANTS } from "../fields/faq/faq_1";
import { FAQ_2_CONSTANTS } from "../fields/faq/faq_2";
import { FAQ_3_CONSTANTS } from "../fields/faq/faq_3";
import { FAQ_4_CONSTANTS } from "../fields/faq/faq_4";
import { VISIONARIES_1_CONSTANTS } from "../fields/visionaries/visionaries_1";
import { PROGRAM_STRUCTURE_1_CONSTANTS } from "../fields/programStructure/programStructure_1";
import { ELECTIVE_1_CONSTANTS } from "../fields/elective/elective_1";
import { PROGRAM_FEE_1_CONSTANTS } from "../fields/programFee/programFee_1";
import { PROGRAM_FEE_1043_CONSTANTS } from "../fields/programFee/programFee_1043";
import { PROGRAM_FEE_2_CONSTANTS } from "../fields/programFee/programFee_2";
import { PROGRAM_STRUCTURE_2_CONSTANTS } from "../fields/programStructure/programStructure_2";
import { PROGRAM_STRUCTURE_3_CONSTANTS } from "../fields/programStructure/programStructure_3";
import { PROGRAM_STRUCTURE_4_CONSTANTS } from "../fields/programStructure/programStructure_4";
import { PROGRAM_STRUCTURE_5_CONSTANTS } from "../fields/programStructure/programStructure_5";
import { PROGRAM_STRUCTURE_1042_CONSTANTS } from "../fields/programStructure/programStructure_1042";
import { PROGRAM_STRUCTURE_1064_CONSTANTS } from "../fields/programStructure/programStructure_1064";
import { ELECTIVE_1065_CONSTANTS } from "../fields/elective/elective_1065";
import { PROGRAM_FEE_1066_CONSTANTS } from "../fields/programFee/programFee_1066";
import ProgramStructure1064 from "@/widgets/programstructure/ProgramStructure1064";
import Elective1065 from "@/widgets/elective/Elective1065";
import ProgramFee1066 from "@/widgets/programfee/ProgramFee1066";
import {
  createFaqCategory,
  updateFaqCategory,
  deleteFaqCategory,
  getFaqCategoriesByComponentId,
  getFaqByComponentId,
  createFaqItem,
  updateFaqItem,
  deleteFaqItem,
  getFaqItemsByCategoryId,
  cloneFaqWithCategories,
} from "@/api/admin/faq";
import {
  createVisionariesCategory,
  updateVisionariesCategory,
  deleteVisionariesCategory,
  getVisionariesCategoriesByComponentId,
  getVisionariesByComponentId,
  createVisionariesProfile,
  updateVisionariesProfile,
  deleteVisionariesProfile,
  getVisionariesProfilesByCategoryId,
  cloneVisionariesWithCategories,
} from "@/api/admin/visionaries";
import {
  createSemester,
  updateSemester,
  deleteSemester,
  getSemestersByComponentId,
  getProgramStructureByComponentId,
  createSubject,
  updateSubject,
  deleteSubject,
  getSubjectsBySemesterId,
  cloneProgramStructureWithSemesters,
} from "@/api/admin/programStructure";
import {
  createElectiveCategory,
  updateElectiveCategory,
  deleteElectiveCategory,
  getElectiveCategoriesByComponentId,
  createElectiveSemester,
  updateElectiveSemester,
  deleteElectiveSemester,
  getElectiveSemestersByCategoryId,
  createElectiveSubject,
  updateElectiveSubject,
  deleteElectiveSubject,
  getElectiveSubjectsBySemesterId,
  getElectiveByComponentId,
  cloneElectiveWithCategories,
} from "@/api/admin/elective";
import {
  createProgramFee,
  updateProgramFee,
  getProgramFeeDataByComponentId,
  createFeeType,
  updateFeeType,
  deleteFeeType,
  getFeeTypesByComponentId,
  createFeeCategory,
  updateFeeCategory,
  deleteFeeCategory,
  getFeeCategoriesByComponentId,
  createFeeSemester,
  updateFeeSemester,
  deleteFeeSemester,
  getFeeSemestersByComponentId,
  createFeeValue,
  updateFeeValue,
  deleteFeeValue,
  getFeeValuesByComponentId,
  cloneProgramFeeWithNestedData,
  getProgramFeeByComponentId,
} from "@/api/admin/programFee";
import {
  createProgramFeeV2,
  updateProgramFeeV2,
  getProgramFeeV2DataByComponentId,
  getProgramFeeV2ByComponentId,
} from "@/api/admin/programFeeV2";
import {
  createProgramStructureV2,
  updateProgramStructureV2,
  getProgramStructureV2DataByComponentId,
  getProgramStructureV2ByComponentId,
  cloneProgramStructureV2WithNestedData,
  createProgramStructureV2Semester,
  updateProgramStructureV2Semester,
  deleteProgramStructureV2Semester,
  getProgramStructureV2SemestersByComponentId,
  createProgramStructureV2Category,
  updateProgramStructureV2Category,
  deleteProgramStructureV2Category,
  getProgramStructureV2CategoriesBySemesterId,
  createProgramStructureV2CategoryItem,
  updateProgramStructureV2CategoryItem,
  deleteProgramStructureV2CategoryItem,
  getProgramStructureV2CategoryItemsByCategoryId,
  createProgramStructureV2Subject,
  updateProgramStructureV2Subject,
  deleteProgramStructureV2Subject,
  getProgramStructureV2SubjectsBySemesterId,
} from "@/api/admin/programStructureV2";
import Faqs from "@/widgets/faqs/Faqs1";
import Faqs2 from "@/widgets/faqs/Faqs2";
import Visionaries1 from "@/widgets/visionaries/Visionaries1";
import Faqs3 from "@/widgets/faqs/Faqs3";
import Faqs4 from "@/widgets/faqs/Faqs4";
import ProgramStructure1 from "@/widgets/programstructure/ProgramStructure1";
import Elective1 from "@/widgets/elective/Elective1";
import ProgramFee1 from "@/widgets/programfee/ProgramFee1";
import ProgramFee1043 from "@/widgets/programfee/ProgramFee1043";
import ProgramFee2 from "@/widgets/programfee/ProgramFee2";
import ProgramFee2Variant3 from "@/widgets/programfee/ProgramFee2Variant3";
import { ProgramStructure2 } from "@/widgets/programstructure/ProgramStructure2";
import ProgramStructure3 from "@/widgets/programstructure/ProgramStructure3";
import ProgramStructure4 from "@/widgets/programstructure/ProgramStructure4";
import ProgramStructure5 from "@/widgets/programstructure/ProgramStructure5";
import ProgramStructure1042 from "@/widgets/programstructure/ProgramStructure1042";
import {
  SECTION_TYPES,
  FAQ_VARIANTS,
  VISIONARIES_VARIANTS,
  PROGRAM_STRUCTURE_VARIANTS,
  ELECTIVE_VARIANTS,
  PROGRAM_FEE_VARIANTS,
} from "../sectionTypes";
import {
  createFaqComponent,
  updateFaqComponent,
  createVisionariesComponent,
  updateVisionariesComponent,
  createProgramStructureComponent,
  updateProgramStructureComponent,
  createElectiveComponent,
  updateElectiveComponent,
  extractMetadataFields,
} from "@/helpers";
import { activateMetadata, getPreviewData } from "@/utils/variantHelpers";

/** FAQ widgets (Form_3): merge section.data with live Redux so heading/colors update while editing */
const buildFaqPreviewProps = ({ section, reduxState, isSelected }: any) => {
  const merged = getPreviewData(section, reduxState, isSelected);
  const categories = Array.isArray(merged?.categories) ? merged.categories : [];
  return {
    data: {
      data: {
        ...merged,
        categories,
        status: merged?.status !== undefined && merged?.status !== null ? merged.status : true,
      },
    },
  };
};
import { PROGRAM_FEE_3_CONSTANTS } from "../fields/programFee/programFee_3";
import { FAQ_5013_CONSTANTS } from "../fields/faq/faq_5013";
import { FAQ_1033_CONSTANTS } from "../fields/faq/faq_1033";
import Faqs5013 from "@/widgets/faqs/Faqs5013";
import Faqs1033 from "@/widgets/faqs/Faqs1033";

export const CURRICULUM_SECTION_VARIANTS = {
  [SECTION_TYPES.FAQ]: [
    {
      id: FAQ_VARIANTS.FAQ_1,
      name: "FAQ 1",
      description: "Frequently Asked Questions with categories",
      componentFields: FAQ_1_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_1_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_1_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq1,
      formComponent: Form_3,
      previewComponent: Faqs,
      selector: (state: PageState) => state.faq_1,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: buildFaqPreviewProps,
    },
    {
      id: FAQ_VARIANTS.FAQ_2,
      name: "FAQ 2",
      description: "Frequently Asked Questions with categories",
      componentFields: FAQ_2_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_2_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_2_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq2,
      formComponent: Form_3,
      previewComponent: Faqs2,
      selector: (state: PageState) => state.faq_2,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        const categories =
          data.categories && Array.isArray(data.categories)
            ? data.categories
            : [];

        return {
          data: {
            data: {
              ...data,
              categories,
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              status: true,
            },
          },
        };
      },
    },
    {
      id: FAQ_VARIANTS.FAQ_3,
      name: "FAQ 3 — Accordion",
      description:
        "FAQ 3 — Accordion",
      componentFields: FAQ_3_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_3_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_3_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq3,
      formComponent: Form_3,
      previewComponent: Faqs3,
      selector: (state: PageState) => state.faq_3,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: buildFaqPreviewProps,
    },
    {
      id: FAQ_VARIANTS.FAQ_4,
      name: "FAQ 4 — Explore more cards",
      description: "FAQ 4 — background-image section with a stack of expandable cards",
      componentFields: FAQ_4_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_4_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_4_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq4,
      formComponent: Form_3,
      previewComponent: Faqs4,
      selector: (state: PageState) => state.faq_4,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: buildFaqPreviewProps,
    },
    {
      id: FAQ_VARIANTS.FAQ_1033,
      name: "FAQ 1033 — BDU FAQs",
      description:
        "BDU FAQs — numbered accordion with a tinted open state and Contact Us CTA",
      componentFields: FAQ_1033_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_1033_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_1033_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq1033,
      formComponent: Form_3,
      previewComponent: Faqs1033,
      selector: (state: PageState) => state.faq_1033,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: buildFaqPreviewProps,
    },
    {
      id: FAQ_VARIANTS.FAQ_5013,
      name: "FAQ 5013 — KUK FAQs",
      description:
        "KUK FAQs — a single-open accordion under a ruled heading, tinted open rows on mobile, and a Contact Us CTA on desktop",
      componentFields: FAQ_5013_CONSTANTS.FAQ_FIELDS_CONFIG,
      categoryFieldsConfig: FAQ_5013_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: FAQ_5013_CONSTANTS.FAQ_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setFaq5013,
      formComponent: Form_3,
      previewComponent: Faqs5013,
      selector: (state: PageState) => state.faq_5013,
      createMethod: createFaqComponent,
      updateMethod: updateFaqComponent,
      getMethod: getFaqByComponentId,
      fetchCategoriesMethod: getFaqCategoriesByComponentId,
      createCategoryMethod: createFaqCategory,
      updateCategoryMethod: updateFaqCategory,
      deleteCategoryMethod: deleteFaqCategory,
      fetchItemsMethod: getFaqItemsByCategoryId,
      createItemMethod: createFaqItem,
      updateItemMethod: updateFaqItem,
      deleteItemMethod: deleteFaqItem,
      cloneMethod: cloneFaqWithCategories,
      cloneEntityName: "FAQ",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      preparePreviewData: buildFaqPreviewProps,
    },
  ],
  [SECTION_TYPES.VISIONARIES]: [
    {
      id: VISIONARIES_VARIANTS.VISIONARIES_1,
      name: "Visionaries 1",
      description: "Led by visionaries with tabbed categories and profile cards",
      componentFields: VISIONARIES_1_CONSTANTS.VISIONARIES_FIELDS_CONFIG,
      categoryFieldsConfig: VISIONARIES_1_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      itemTableFieldsConfig: VISIONARIES_1_CONSTANTS.PROFILE_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setVisionaries1,
      formComponent: Form_9,
      previewComponent: Visionaries1,
      selector: (state: PageState) => state.visionaries_1,
      createMethod: createVisionariesComponent,
      updateMethod: updateVisionariesComponent,
      getMethod: getVisionariesByComponentId,
      fetchCategoriesMethod: getVisionariesCategoriesByComponentId,
      createCategoryMethod: createVisionariesCategory,
      updateCategoryMethod: updateVisionariesCategory,
      deleteCategoryMethod: deleteVisionariesCategory,
      fetchItemsMethod: getVisionariesProfilesByCategoryId,
      createItemMethod: createVisionariesProfile,
      updateItemMethod: updateVisionariesProfile,
      deleteItemMethod: deleteVisionariesProfile,
      cloneMethod: cloneVisionariesWithCategories,
      cloneEntityName: "Visionaries",
      cloneNestedEntities: "categories",
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "category_id",
      supportsOptionalCategoriesToggle: true,
      visionariesHybridForm: true,
      visionariesPoolOnlyItems: true,
      hideCategoryItems: true,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const persisted =
          section?.data && typeof section.data === "object" ? section.data : {};
        const unwrapped =
          persisted.data &&
          typeof persisted.data === "object" &&
          (Array.isArray((persisted.data as any).categories) ||
            Array.isArray((persisted.data as any).pool_profiles))
            ? persisted.data
            : persisted;
        const live = getPreviewData(section, reduxState, isSelected);
        const merged =
          live && typeof live === "object" ? { ...unwrapped, ...live } : { ...unwrapped };

        const categoriesFromMerged = Array.isArray(merged.categories)
          ? merged.categories
          : [];
        const categoriesFromPersisted = Array.isArray(unwrapped.categories)
          ? unwrapped.categories
          : [];
        const rawCategories =
          categoriesFromMerged.length > 0 ? categoriesFromMerged : categoriesFromPersisted;

        const poolFromMerged = Array.isArray(merged.pool_profiles)
          ? merged.pool_profiles
          : [];
        const poolFromPersisted = Array.isArray(unwrapped.pool_profiles)
          ? unwrapped.pool_profiles
          : [];
        const poolProfiles =
          poolFromMerged.length > 0 ? poolFromMerged : poolFromPersisted;

        const categories = rawCategories.map((cat: any) => ({
          ...cat,
          profiles: Array.isArray(cat.profiles)
            ? cat.profiles
            : Array.isArray(cat.items)
              ? cat.items
              : [],
        }));

        const data = { ...merged, categories, pool_profiles: poolProfiles };
        return {
          data: {
            data: {
              ...data,
              categories,
              pool_profiles: poolProfiles,
              enable_animation: Boolean(
                data.enable_animation === true || data.enable_animation === 1,
              ),
              enable_coverflow_carousel: !(
                data.enable_coverflow_carousel === false ||
                data.enable_coverflow_carousel === 0
              ),
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PROGRAM_STRUCTURE]: [
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_1,
      name: "Program Structure 1",
      description: "Program Structure with semesters and subjects",
      componentFields:
        PROGRAM_STRUCTURE_1_CONSTANTS.PROGRAM_STRUCTURE_FIELDS_CONFIG,
      categoryFieldsConfig:
        PROGRAM_STRUCTURE_1_CONSTANTS.SEMESTER_FIELDS_CONFIG,
      itemTableFieldsConfig:
        PROGRAM_STRUCTURE_1_CONSTANTS.SUBJECT_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setProgramStructure1,
      formComponent: Form_3,
      previewComponent: ProgramStructure1,
      selector: (state: PageState) => state.program_structure_1,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      fetchCategoriesMethod: getSemestersByComponentId,
      createCategoryMethod: createSemester,
      updateCategoryMethod: updateSemester,
      deleteCategoryMethod: deleteSemester,
      fetchItemsMethod: getSubjectsBySemesterId,
      createItemMethod: createSubject,
      updateItemMethod: updateSubject,
      deleteItemMethod: deleteSubject,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      hasParentChildStructure: true,
      childItemsFieldName: "semesters",
      itemParentIdFieldName: "semester_id",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const rawData = section.data || {};

        // Handle nested API response structure (response has success, data, etc)
        const sectionUnwrapped =
          rawData.data && rawData.data.semesters ? rawData.data : rawData;

        // Prefer Redux while this widget is selected so preview updates as you type (matches Form_1 / getPreviewData).
        // section.data is only refreshed on save (Update Component / tree saves); without this, preview lagged until a button click.
        const live = getPreviewData(section, reduxState, isSelected);

        const semesters = Array.isArray(live?.semesters)
          ? live.semesters
          : Array.isArray(sectionUnwrapped?.semesters)
            ? sectionUnwrapped.semesters
            : [];

        return {
          data: {
            data: {
              ...sectionUnwrapped,
              ...live,
              semesters,
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_2,
      name: "Program Structure 2",
      description:
        "Curriculum-style program structure with semesters, categories, category items, subjects, electives, tools, and skills",
      componentFields: PROGRAM_STRUCTURE_2_CONSTANTS.PROGRAM_STRUCTURE_V2_FIELDS_CONFIG,
      // Semester fields (Level 1 in the semester tree)
      semesterFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.SEMESTER_FIELDS_CONFIG,
      // Category fields (independent entity on program_structure_v2)
      categoryFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      // Category Item fields (children of categories)
      categoryItemFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.CATEGORY_ITEM_FIELDS_CONFIG,
      // Subject fields
      subjectFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.SUBJECT_FIELDS_CONFIG,
      // JSON list configs
      toolFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.TOOL_FIELDS_CONFIG,
      skillFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.SKILL_FIELDS_CONFIG,
      electiveFieldsConfig: PROGRAM_STRUCTURE_2_CONSTANTS.ELECTIVE_FIELDS_CONFIG,
      setAction: setProgramStructure2,
      formComponent: Form_7,
      previewComponent: ProgramStructure2,
      selector: (state: PageState) => state.program_structure_2,
      // API Methods - Main
      createMethod: createProgramStructureV2,
      updateMethod: updateProgramStructureV2,
      getMethod: getProgramStructureV2DataByComponentId,
      // Semester Methods
      fetchSemestersMethod: getProgramStructureV2SemestersByComponentId,
      createSemesterMethod: createProgramStructureV2Semester,
      updateSemesterMethod: updateProgramStructureV2Semester,
      deleteSemesterMethod: deleteProgramStructureV2Semester,
      // Category Methods (belong to semesters, can vary per semester)
      fetchCategoriesMethod: getProgramStructureV2CategoriesBySemesterId,
      createCategoryMethod: createProgramStructureV2Category,
      updateCategoryMethod: updateProgramStructureV2Category,
      deleteCategoryMethod: deleteProgramStructureV2Category,
      // Category Item Methods
      fetchCategoryItemsMethod: getProgramStructureV2CategoryItemsByCategoryId,
      createCategoryItemMethod: createProgramStructureV2CategoryItem,
      updateCategoryItemMethod: updateProgramStructureV2CategoryItem,
      deleteCategoryItemMethod: deleteProgramStructureV2CategoryItem,
      // Subject Methods
      fetchSubjectsMethod: getProgramStructureV2SubjectsBySemesterId,
      createSubjectMethod: createProgramStructureV2Subject,
      updateSubjectMethod: updateProgramStructureV2Subject,
      deleteSubjectMethod: deleteProgramStructureV2Subject,
      // Clone
      cloneMethod: cloneProgramStructureV2WithNestedData,
      cloneEntityName: "Program Structure V2",
      cloneNestedEntities: "semesters, categories, category items, subjects, electives, tools, and skills",
      cloneGetSourceId: getProgramStructureV2ByComponentId,
      hasParentChildStructure: true,
      childItemsFieldName: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              semesters: data.semesters || [],
              subjects: data.subjects || [],
              tools: data.tools || [],
              skills: data.skills || [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_3,
      name: "Program Structure — module explorer",
      description:
        "Two-column curriculum: scrollable module list + topic detail panel",
      componentFields:
        PROGRAM_STRUCTURE_3_CONSTANTS.PROGRAM_STRUCTURE_3_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_STRUCTURE_3_CONSTANTS),
      showTable: false,
      setAction: setProgramStructure3,
      formComponent: Form_1,
      previewComponent: ProgramStructure3,
      selector: (state: PageState) => state.program_structure_3,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta = activateMetadata(data.metadata || []);
        const hasRows = meta.some(
          (m: { module_title?: string }) =>
            String(m?.module_title ?? "").trim().length > 0,
        );
        const metadata = hasRows
          ? meta
          : activateMetadata([
              {
                ...PROGRAM_STRUCTURE_3_CONSTANTS.DEFAULT_ROW,
                module_title: "Module 1: Sample module",
                topics_html:
                  "<p>Add modules in the admin table. Each row appears in the left list; topics HTML shows on the right.</p>",
              },
            ]);
        return {
          data: {
            data: {
              ...data,
              metadata,
              status: data.status !== false,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_4,
      name: "Program Structure — semester syllabus",
      description:
        "Background banner with semester tabs and a credit-wise subject list (Apply / Download Brochure CTAs)",
      componentFields:
        PROGRAM_STRUCTURE_4_CONSTANTS.PROGRAM_STRUCTURE_4_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_STRUCTURE_4_CONSTANTS),
      showTable: false,
      setAction: setProgramStructure4,
      formComponent: Form_1,
      previewComponent: ProgramStructure4,
      selector: (state: PageState) => state.program_structure_4,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta = activateMetadata(data.metadata || []);
        const hasRows = meta.some(
          (m: { semester_name?: string; subject_name?: string }) =>
            String(m?.semester_name ?? "").trim().length > 0 &&
            String(m?.subject_name ?? "").trim().length > 0,
        );
        const metadata = hasRows
          ? meta
          : activateMetadata([
              {
                ...PROGRAM_STRUCTURE_4_CONSTANTS.DEFAULT_ROW,
                semester_name: "Semester 1",
                subject_name: "Add subjects in the admin table",
                credits: "4",
              },
            ]);
        return {
          data: {
            data: {
              ...data,
              metadata,
              status: data.status !== false,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_1042,
      name: "Program Structure 1042 — BDU Programme Syllabus",
      description:
        "BDU Programme Syllabus — tinted left column with semester tabs, subject list, elective picker and brochure CTA",
      componentFields:
        PROGRAM_STRUCTURE_1042_CONSTANTS.PROGRAM_STRUCTURE_1042_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_STRUCTURE_1042_CONSTANTS),
      showTable: true,
      setAction: setProgramStructure1042,
      formComponent: Form_1,
      previewComponent: ProgramStructure1042,
      selector: (state: PageState) => state.program_structure_1042,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget filters status-false rows itself,
              // so the preview sees exactly what the live page groups into tabs.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_1064,
      name: "Program Structure 1064 — KUK Programme Structure",
      description:
        "KUK Programme Structure — fixed background art behind a heading and brochure CTA, beside a single-open semester accordion of subject tables",
      componentFields:
        PROGRAM_STRUCTURE_1064_CONSTANTS.PROGRAM_STRUCTURE_1064_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_STRUCTURE_1064_CONSTANTS),
      showTable: true,
      setAction: setProgramStructure1064,
      formComponent: Form_1,
      previewComponent: ProgramStructure1064,
      selector: (state: PageState) => state.program_structure_1064,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget filters status-false rows itself,
              // so the preview sees exactly what the live page groups into sections.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_STRUCTURE_VARIANTS.PROGRAM_STRUCTURE_5,
      name: "Program Structure — key skills grid",
      description:
        "Full-bleed banner with a headline and a centered grid of icon cards (key skills / highlights)",
      componentFields:
        PROGRAM_STRUCTURE_5_CONSTANTS.PROGRAM_STRUCTURE_5_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_STRUCTURE_5_CONSTANTS),
      showTable: false,
      setAction: setProgramStructure5,
      formComponent: Form_1,
      previewComponent: ProgramStructure5,
      selector: (state: PageState) => state.program_structure_5,
      createMethod: createProgramStructureComponent,
      updateMethod: updateProgramStructureComponent,
      getMethod: getProgramStructureByComponentId,
      cloneMethod: cloneProgramStructureWithSemesters,
      cloneEntityName: "Program Structure",
      cloneNestedEntities: "semesters",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        const meta = activateMetadata(data.metadata || []);
        const hasRows = meta.some(
          (m: { card_title?: string }) =>
            String(m?.card_title ?? "").trim().length > 0,
        );
        const metadata = hasRows
          ? meta
          : activateMetadata([
              {
                ...PROGRAM_STRUCTURE_5_CONSTANTS.DEFAULT_ROW,
                card_title: "Add skill cards in the admin table",
                card_description:
                  "Each row becomes a card with an icon, title and description.",
              },
            ]);
        return {
          data: {
            data: {
              ...data,
              metadata,
              status: data.status !== false,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.ELECTIVE]: [
    {
      id: ELECTIVE_VARIANTS.ELECTIVE_1,
      name: "Elective 1",
      description: "Electives with categories, semesters and subjects",
      componentFields: ELECTIVE_1_CONSTANTS.ELECTIVE_FIELDS_CONFIG,
      categoryFieldsConfig: ELECTIVE_1_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      nestedItemFieldsConfig: ELECTIVE_1_CONSTANTS.SEMESTER_FIELDS_CONFIG,
      itemTableFieldsConfig: ELECTIVE_1_CONSTANTS.SUBJECT_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setElective1,
      formComponent: Form_3,
      previewComponent: Elective1,
      selector: (state: PageState) => state.elective_1,
      createMethod: createElectiveComponent,
      updateMethod: updateElectiveComponent,
      getMethod: getElectiveByComponentId,
      fetchCategoriesMethod: getElectiveCategoriesByComponentId,
      createCategoryMethod: createElectiveCategory,
      updateCategoryMethod: updateElectiveCategory,
      deleteCategoryMethod: deleteElectiveCategory,
      fetchItemsMethod: getElectiveSubjectsBySemesterId,
      createItemMethod: createElectiveSubject,
      updateItemMethod: updateElectiveSubject,
      deleteItemMethod: deleteElectiveSubject,
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "semester_id",
      fetchNestedItemsMethod: getElectiveSemestersByCategoryId,
      createNestedItemMethod: createElectiveSemester,
      updateNestedItemMethod: updateElectiveSemester,
      deleteNestedItemMethod: deleteElectiveSemester,
      nestedItemParentIdFieldName: "category_id",
      nestedItemsFieldName: "semesters",
      cloneMethod: cloneElectiveWithCategories,
      cloneEntityName: "Elective",
      cloneNestedEntities: "categories, semesters, and subjects",
      preparePreviewData: ({ section }: any) => {
        const rawData = section.data || {};

        // Handle nested API response structure (response has success, data, etc)
        // If data has a nested 'data' property with categories, unwrap it
        const data =
          rawData.data && rawData.data.categories ? rawData.data : rawData;

        const categories =
          data.categories && Array.isArray(data.categories)
            ? data.categories
            : [];

        return {
          data: {
            data: {
              ...data,
              categories,
              status: true,
            },
          },
        };
      },
    },
    {
      id: ELECTIVE_VARIANTS.ELECTIVE_1065,
      name: "Elective 1065 — KUK Electives",
      description:
        "KUK Electives — elective tabs down the left, a subject/semester table on the right. Category = one elective, semester = the table's right-hand column, subject = a row.",
      componentFields: ELECTIVE_1065_CONSTANTS.ELECTIVE_FIELDS_CONFIG,
      categoryFieldsConfig: ELECTIVE_1065_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      nestedItemFieldsConfig: ELECTIVE_1065_CONSTANTS.SEMESTER_FIELDS_CONFIG,
      itemTableFieldsConfig: ELECTIVE_1065_CONSTANTS.SUBJECT_TABLE_FIELDS_CONFIG,
      showTable: false,
      setAction: setElective1065,
      formComponent: Form_3,
      previewComponent: Elective1065,
      selector: (state: PageState) => state.elective_1065,
      createMethod: createElectiveComponent,
      updateMethod: updateElectiveComponent,
      getMethod: getElectiveByComponentId,
      fetchCategoriesMethod: getElectiveCategoriesByComponentId,
      createCategoryMethod: createElectiveCategory,
      updateCategoryMethod: updateElectiveCategory,
      deleteCategoryMethod: deleteElectiveCategory,
      fetchItemsMethod: getElectiveSubjectsBySemesterId,
      createItemMethod: createElectiveSubject,
      updateItemMethod: updateElectiveSubject,
      deleteItemMethod: deleteElectiveSubject,
      hasParentChildStructure: true,
      childItemsFieldName: "categories",
      itemParentIdFieldName: "semester_id",
      fetchNestedItemsMethod: getElectiveSemestersByCategoryId,
      createNestedItemMethod: createElectiveSemester,
      updateNestedItemMethod: updateElectiveSemester,
      deleteNestedItemMethod: deleteElectiveSemester,
      nestedItemParentIdFieldName: "category_id",
      nestedItemsFieldName: "semesters",
      cloneMethod: cloneElectiveWithCategories,
      cloneEntityName: "Elective",
      cloneNestedEntities: "categories, semesters, and subjects",
      preparePreviewData: ({ section }: any) => {
        const rawData = section.data || {};

        // Handle nested API response structure (response has success, data, etc)
        // If data has a nested 'data' property with categories, unwrap it
        const data =
          rawData.data && rawData.data.categories ? rawData.data : rawData;

        const categories =
          data.categories && Array.isArray(data.categories)
            ? data.categories
            : [];

        return {
          data: {
            data: {
              ...data,
              categories,
              status: true,
            },
          },
        };
      },
    },
  ],
  [SECTION_TYPES.PROGRAM_FEE]: [
    {
      id: PROGRAM_FEE_VARIANTS.PROGRAM_FEE_1043,
      name: "Program Fee 1043 — BDU Fee Structure",
      description:
        "BDU Fee Structure — student-type pills, a fee-plan list and a line-item table with a summed total. Same data as Program Fee 1: Fee Type = student type, Semester = fee plan, Category = line item.",
      componentFields: PROGRAM_FEE_1043_CONSTANTS.PROGRAM_FEE_FIELDS_CONFIG,
      setAction: setProgramFee1043,
      formComponent: Form_6,
      previewComponent: ProgramFee1043,
      selector: (state: PageState) => state.program_fee_1043,
      // Form_6 config — relabelled for BDU's shape
      tabsLabel: "Student Types",
      tabsSingularLabel: "Student Type",
      rowsLabel: "Line Items",
      rowsSingularLabel: "Line Item",
      columnsLabel: "Fee Plans",
      columnsSingularLabel: "Fee Plan",
      categoryHeaderLabel: "Line Item",
      tabsIcon: "💰",
      showTabStyling: false,
      showRowStyling: false,
      showColumnStyling: false,
      showCellStyling: false,
      showCategoryHeader: false,
      showBorderColor: false,
      // API Methods
      createMethod: createProgramFee,
      updateMethod: updateProgramFee,
      getMethod: getProgramFeeDataByComponentId,
      createTabMethod: createFeeType,
      updateTabMethod: updateFeeType,
      deleteTabMethod: deleteFeeType,
      createRowMethod: createFeeCategory,
      updateRowMethod: updateFeeCategory,
      deleteRowMethod: deleteFeeCategory,
      createColumnMethod: createFeeSemester,
      updateColumnMethod: updateFeeSemester,
      deleteColumnMethod: deleteFeeSemester,
      createValueMethod: createFeeValue,
      updateValueMethod: updateFeeValue,
      cloneMethod: cloneProgramFeeWithNestedData,
      cloneEntityName: "Program Fee",
      cloneNestedEntities: "fee types, categories, semesters, and values",
      cloneGetSourceId: getProgramFeeByComponentId,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = isSelected && reduxState ? reduxState : section.data || {};
        return {
          data: {
            data: {
              ...data,
              feeTypes: data.feeTypes || data.tabs || [],
              categories: data.categories || data.rows || [],
              semesters: data.semesters || data.columns || [],
              feeValues: data.feeValues || data.values || [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_FEE_VARIANTS.PROGRAM_FEE_1,
      name: "Program Fee 1",
      description:
        "Program fee structure with fee types, categories, semesters, and values",
      componentFields: PROGRAM_FEE_1_CONSTANTS.PROGRAM_FEE_FIELDS_CONFIG,
      setAction: setProgramFee1,
      formComponent: Form_6,
      previewComponent: ProgramFee1,
      selector: (state: PageState) => state.program_fee_1,
      // Form_6 config
      tabsLabel: "Fee Types",
      tabsSingularLabel: "Fee Type",
      rowsLabel: "Categories",
      rowsSingularLabel: "Category",
      columnsLabel: "Semesters",
      columnsSingularLabel: "Semester",
      categoryHeaderLabel: "Category",
      tabsIcon: "💰",
      showTabStyling: true,
      showRowStyling: true,
      showColumnStyling: true,
      showCellStyling: true,
      showCategoryHeader: true,
      // Hide border color option for program fee
      showBorderColor: false,
      // API Methods
      createMethod: createProgramFee,
      updateMethod: updateProgramFee,
      getMethod: getProgramFeeDataByComponentId,
      // Tab (Fee Type) Methods
      createTabMethod: createFeeType,
      updateTabMethod: updateFeeType,
      deleteTabMethod: deleteFeeType,
      // Row (Category) Methods
      createRowMethod: createFeeCategory,
      updateRowMethod: updateFeeCategory,
      deleteRowMethod: deleteFeeCategory,
      // Column (Semester) Methods
      createColumnMethod: createFeeSemester,
      updateColumnMethod: updateFeeSemester,
      deleteColumnMethod: deleteFeeSemester,
      // Value Methods
      createValueMethod: createFeeValue,
      updateValueMethod: updateFeeValue,
      // Clone
      cloneMethod: cloneProgramFeeWithNestedData,
      cloneEntityName: "Program Fee",
      cloneNestedEntities: "fee types, categories, semesters, and values",
      cloneGetSourceId: getProgramFeeByComponentId,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        // Use reduxState when selected for live updates, otherwise use section.data
        const data = isSelected && reduxState ? reduxState : section.data || {};
        return {
          data: {
            data: {
              ...data,
              feeTypes: data.feeTypes || data.tabs || [],
              categories: data.categories || data.rows || [],
              semesters: data.semesters || data.columns || [],
              feeValues: data.feeValues || data.values || [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_FEE_VARIANTS.PROGRAM_FEE_2,
      name: "Program Fee 2",
      description:
        "Flexible fee section with gradient titles, category tabs, metadata lists, and feature cards",
      componentFields: PROGRAM_FEE_2_CONSTANTS.PROGRAM_FEE_FIELDS_CONFIG,
      metadataFields: PROGRAM_FEE_2_CONSTANTS.METADATA_FIELDS_CONFIG,
      metadata1Fields: PROGRAM_FEE_2_CONSTANTS.METADATA_1_FIELDS_CONFIG,
      categoryMetadataFields: PROGRAM_FEE_2_CONSTANTS.CATEGORY_METADATA_FIELDS_CONFIG,
      categoryFieldsConfig: PROGRAM_FEE_2_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      setAction: setProgramFee2,
      formComponent: Form_8,
      previewComponent: ProgramFee2,
      selector: (state: PageState) => state.program_fee_2,
      // API Methods
      createMethod: createProgramFeeV2,
      updateMethod: updateProgramFeeV2,
      getMethod: getProgramFeeV2DataByComponentId,
      cloneGetSourceId: getProgramFeeV2ByComponentId,
      cloneEntityName: "Program Fee V2",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = isSelected && reduxState ? reduxState : section.data || {};
        return {
          data: {
            data: {
              ...data,
              status: 1,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_FEE_VARIANTS.PROGRAM_FEE_3,
      name: "Program Fee 3",
      description:
        "Light/maroon 'Program Fees' design with multi-level fee tabs, metadata lists, and feature cards",
      // Shares Program Fee 2's data + backend, but renders the dedicated
      // ProgramFee2Variant3 ("Program Fees") component directly.
      componentFields: PROGRAM_FEE_3_CONSTANTS.PROGRAM_FEE_FIELDS_CONFIG,
      metadataFields: PROGRAM_FEE_3_CONSTANTS.METADATA_FIELDS_CONFIG,
      metadata1Fields: PROGRAM_FEE_3_CONSTANTS.METADATA_1_FIELDS_CONFIG,
      categoryMetadataFields: PROGRAM_FEE_3_CONSTANTS.CATEGORY_METADATA_FIELDS_CONFIG,
      categoryFieldsConfig: PROGRAM_FEE_3_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      setAction: setProgramFee2,
      showCategoryColors: false,
      formComponent: Form_8,
      previewComponent: ProgramFee2Variant3,
      selector: (state: PageState) => state.program_fee_2,
      createMethod: createProgramFeeV2,
      updateMethod: updateProgramFeeV2,
      getMethod: getProgramFeeV2DataByComponentId,
      cloneGetSourceId: getProgramFeeV2ByComponentId,
      cloneEntityName: "Program Fee V2",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = isSelected && reduxState ? reduxState : section.data || {};
        return {
          data: {
            data: {
              ...data,
              status: 1,
            },
          },
        };
      },
    },
    {
      id: PROGRAM_FEE_VARIANTS.PROGRAM_FEE_1066,
      name: "Program Fee 1066 — KUK Fee Structure",
      description:
        "KUK Fee Structure — student-type tabs over plan cards. Same data as Program Fee 2: one category = one plan card, its Tab Group = the student type, its rows = the amounts (Headline figure on = the big number, off = an additional-fee line), and metadata_1 = the per-tab footnote.",
      componentFields: PROGRAM_FEE_1066_CONSTANTS.PROGRAM_FEE_FIELDS_CONFIG,
      metadataFields: PROGRAM_FEE_1066_CONSTANTS.METADATA_FIELDS_CONFIG,
      metadata1Fields: PROGRAM_FEE_1066_CONSTANTS.METADATA_1_FIELDS_CONFIG,
      categoryMetadataFields:
        PROGRAM_FEE_1066_CONSTANTS.CATEGORY_METADATA_FIELDS_CONFIG,
      categoryFieldsConfig: PROGRAM_FEE_1066_CONSTANTS.CATEGORY_FIELDS_CONFIG,
      setAction: setProgramFee1066,
      formComponent: Form_8,
      previewComponent: ProgramFee1066,
      selector: (state: PageState) => state.program_fee_1066,
      createMethod: createProgramFeeV2,
      updateMethod: updateProgramFeeV2,
      getMethod: getProgramFeeV2DataByComponentId,
      cloneGetSourceId: getProgramFeeV2ByComponentId,
      cloneEntityName: "Program Fee V2",
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = isSelected && reduxState ? reduxState : section.data || {};
        return {
          data: {
            data: {
              ...data,
              status: 1,
            },
          },
        };
      },
    },
  ],
};
